/**
 * MyRacketsPage — standalone "Mis raquetas" section
 * (DASHBOARD-SIDEMENU-PROFILES-1-FIX1 · Feb 2026).
 *
 * Lifts the tickets list out of `RacketLabPlayerPreview.jsx` into its
 * own dedicated route (`/app/my-rackets`) so navigating from the side
 * menu no longer drops the user inside the generic Racket Lab area.
 *
 * Frontend-only · preview/demo · no backend calls · no DB writes.
 * Pulls the player's rackets from the same `localStorage` key
 * (`tsw_player_rackets_v1`) that powers `Mi equipación`.
 */
import React, { useState } from "react";
import PanelLayout from "@/components/layout/PanelLayout";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Wrench, ShieldCheck } from "lucide-react";
import {
  TicketsBlock,
  loadRackets,
  T as RACKET_LAB_T,
} from "@/components/RacketLabPlayerPreview";

const PAGE_COPY = {
  es: {
    eyebrow: "Racket Lab",
    title: "Mis encordados",
    subtitle: "Lista aislada con tus reservas, planificaciones y entradas sin cita.",
    badge: "Preview controlado",
    emptyHint: "Aún no has añadido raquetas en Mi equipación. Las raquetas que añadas aparecerán aquí cuando tengan reservas, planificaciones o entradas sin cita.",
  },
  en: {
    eyebrow: "Racket Lab",
    title: "My stringings",
    subtitle: "Isolated list with your reservations, planned drop-offs and walk-ins.",
    badge: "Controlled preview",
    emptyHint: "You haven't added rackets in My gear yet. Once you do, any reservation or walk-in will show up here.",
  },
};

export default function MyRacketsPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const tx = RACKET_LAB_T[lang] || RACKET_LAB_T.en;
  const copy = PAGE_COPY[lang] || PAGE_COPY.en;

  // Mis raquetas is a Player-only surface today. Other profiles that
  // land on this URL by mistake are redirected back to /app.
  const isPlayer = user?.profile_key === "player";
  const [rackets] = useState(loadRackets);

  if (user && !isPlayer) return <Navigate to="/app" replace />;

  return (
    <PanelLayout>
      <div data-testid="page-my-rackets">
        <header className="flex flex-wrap items-center gap-3 mb-2">
          <Wrench className="w-6 h-6 text-emerald-700" />
          <div className="tsw-overline">{copy.eyebrow}</div>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider"
            data-testid="page-my-rackets-badge"
          >
            <ShieldCheck className="w-3 h-3" />{copy.badge}
          </span>
        </header>
        <h1
          className="text-2xl font-bold text-slate-900"
          data-testid="page-my-rackets-title"
        >
          {copy.title}
        </h1>
        <p className="text-sm text-slate-600 mt-1 mb-6">{copy.subtitle}</p>

        <div
          className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6"
          data-testid="page-my-rackets-tickets-card"
        >
          <TicketsBlock tx={tx} rackets={rackets} lang={lang} />
        </div>

        <p
          className="text-[11px] text-slate-500 mt-4 italic"
          data-testid="page-my-rackets-legal"
        >
          {tx.legalBlurb}
        </p>

        {rackets.length === 0 && (
          <p
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3"
            data-testid="page-my-rackets-empty-hint"
          >
            {copy.emptyHint}
          </p>
        )}
      </div>
    </PanelLayout>
  );
}
